import React, { useMemo } from 'react'

import { Button } from '@barracuda-internal/bds-core'

import { Grid2, TextField, InputAdornment, IconButton } from '@barracuda-internal/bds-core-v2'
import SvgClose from '@barracuda-internal/bds-core-v2/dist/Icons/Core/Close'
import inboundSettingsStyles from 'components/pages/inboundSettings/inboundSettingsStyles'
import { useFormatMessage } from 'lib/localization'
import { GROUP_TITLE, TOPICS } from 'components/pages/support/config'
import { useSenderPoliciesLogic } from 'components/pages/inboundSettings/senderPolicies/useSenderPoliciesLogic'
import InboundSenderPolicies from 'components/pages/support/help/inboundSenderPolicies/InboundSenderPolicies'
import SenderPoliciesTable from 'components/pages/inboundSettings/senderPolicies/SenderPoliciesTable'
import { PageTitle } from 'components/libs/layout/pages/pageLayout'
import { SettingsPageContainer } from 'components/libs/layout/pages/SettingsPageContainer'
import { useIsReadonly } from 'lib/hooks/useIsReadonly'
import NoDomainPolicyAccessAlert from 'components/libs/layout/pages/NoDomainPolicyAccessAlert'

const BASE_INBOUND_I18N_KEY = 'ess.inbound_settings'
const BASE_I18N_KEY = 'ess.inbound_settings.sender_policies'

const SenderPolicies: React.FC = () => {
  const isReadonly = useIsReadonly()
  const [state, eventHandlers] = useSenderPoliciesLogic()
  const inboundClasses = inboundSettingsStyles()
  const formatMessage = useFormatMessage(BASE_I18N_KEY)
  const formatInboundMessage = useFormatMessage(BASE_INBOUND_I18N_KEY)

  return useMemo(
    () => (
      <SettingsPageContainer dirtyCheck isDirtyForm={state.isDirtyForm}>
        <PageTitle
          title={formatMessage('title')}
          onOpenSupport={eventHandlers.helpConfig.onHelpClick}
          help={{
            content: InboundSenderPolicies,
            onClose: eventHandlers.helpConfig.onCloseHelp,
            title: TOPICS.senderPolicies,
            group: GROUP_TITLE.inboundSettings,
            isHelpOpen: eventHandlers.helpConfig.isOpen
          }}
        />
        {state.hasNoDomainPolicyAccess && <NoDomainPolicyAccessAlert />}
        {!state.hasNoDomainPolicyAccess && (
          <Grid2 container>
            <Grid2 display="flex" alignContent="flex-end" alignItems="center" width="100%" marginY="16px">
              <TextField
                placeholder={formatMessage('search_placeholder')}
                fullWidth
                sx={{ marginRight: '16px' }}
                onChange={state.pagination.onSearchKeywordChange}
                value={state.pagination.searchKeyword}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton bdsSize="medium" onClick={state.pagination.onClearSearchKeyword}>
                        <SvgClose />
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
              <Button
                className={inboundClasses.bulkEdit}
                onClick={eventHandlers.onBulkEdit}
                variant="contained"
                size="small"
                data-testid="bulk-button"
                disabled={isReadonly}
                style={{ whiteSpace: 'nowrap', margin: 0 }}
              >
                {formatInboundMessage('bulk_edit')}
              </Button>
            </Grid2>
            <SenderPoliciesTable
              pagination={state.pagination}
              availablePolicies={state.availablePolicies}
              onAddItem={eventHandlers.onAdd}
              onRemoveItem={eventHandlers.onRemove}
              delegateDirtyFormState={eventHandlers.setDirtyForm}
            />
          </Grid2>
        )}
      </SettingsPageContainer>
    ),
    [
      state.isDirtyForm,
      state.pagination,
      state.availablePolicies,
      formatMessage,
      eventHandlers.helpConfig.onHelpClick,
      eventHandlers.helpConfig.onCloseHelp,
      eventHandlers.helpConfig.isOpen,
      eventHandlers.onBulkEdit,
      eventHandlers.onAdd,
      eventHandlers.onRemove,
      eventHandlers.setDirtyForm,
      inboundClasses.bulkEdit,
      isReadonly,
      formatInboundMessage,
      state.hasNoDomainPolicyAccess
    ]
  )
}

export default SenderPolicies
